import "./missionDetailsCard.css";

import {
  Badge,
  Button,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import {
  MISSION_MONITORING_FLAG_COLORS,
  MISSION_MONITORING_STATIC_FLAGS,
  MISSION_MONITORING_THUMB_FLAG,
  MISSION_MONITORING_THUMB_STEP,
} from "../../../constants";
import {
  dateSort,
  formatDate,
  getContrastYIQ,
  getFlagColor,
  humanize,
} from "../../../utils";
import {
  fetchHccMission,
  fetchUploadSession,
  requestCreateMissionsMonitoringFlag,
  requestDeleteMissionMonitoring,
  requestDeleteMissionsMonitoringFlag,
  requestFetchMissionMonitoring,
  requestUpdateMissionsMonitoringFlag,
} from "../../../services/backendRequests";
import { hasAdminAccess, hasBackofficeAccess } from "../../../services/roles";
import { useEffect, useMemo, useRef, useState } from "react";

import { AcquisitionReport } from "../acquisitionReport/AcquisitionReport";
import { ComponentPlaceholder } from "../../../components/componentPlaceholder/componentPlaceholder";
import { ComponentTitle } from "../../../components/componentTitle/componentTitle";
import CopyButton from "../../../components/copyButton";
import { ExpandButton } from "../../../components/expandButton/expandButton";
import { MissionChat } from "../missionChat/missionChat";
import PropTypes from "prop-types";
import { CfSelect as ReactSelect } from "../../../components/CfSelect";
import UploadSessionModal from "../../../uploadsessions/uploadSessionModal";
import classnames from "classnames";
import moment from "moment";

/*
 * Timeline of mm, dates on steps and flagss
 */

const HistoryTimeLine = ({ color, steps, flags }) => {
  // Concatenation and generisation of flags and steps in a single list and set type flag or step, date

  const timeLineThumbs = [
    ...steps.map((step) => {
      return {
        ...step,
        date: step.date,
        type: MISSION_MONITORING_THUMB_STEP,
        name: humanize(step.name),
      };
    }),
    ...flags.map((flag) => {
      return {
        ...flag,
        date: flag.created_at,
        type: MISSION_MONITORING_THUMB_FLAG,
        name: humanize(flag.name),
      };
    }),
  ].sort((a, b) => dateSort(a.date, b.date));

  const Thumb = ({ thumb }) => {
    Thumb.propTypes = {
      thumb: PropTypes.object.isRequired,
    };

    const iconClasses = classnames("fa-solid", {
      "fa-flag fa-lg": thumb.type === MISSION_MONITORING_THUMB_FLAG,
    });

    const thumbColor =
      thumb.type === MISSION_MONITORING_THUMB_FLAG ? "none" : color;
    const iconColor =
      thumb.type === MISSION_MONITORING_THUMB_FLAG
        ? getFlagColor(thumb)
        : getContrastYIQ(thumbColor);

    return (
      <div className="thumb-wrapper">
        <span className="thumb-date">{formatDate(thumb.date)}</span>
        <div
          className="thumb"
          style={{ background: thumbColor }}
          data-tooltip-id="tooltip"
          data-tooltip-html={`${formatDate(thumb.date)}<br>${thumb.name}`}
        >
          <div>
            <i className={iconClasses} style={{ color: iconColor }}></i>
          </div>
        </div>

        <span className="thumb-label">{thumb.name}</span>
      </div>
    );
  };

  // Click-and-drag ("grab hand") horizontal panning; the native scrollbar is
  // hidden in CSS so this is the only way to move along the timeline.
  const scrollRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0 });

  const startDrag = (event) => {
    const el = scrollRef.current;
    drag.current = {
      active: true,
      startX: event.pageX,
      scrollLeft: el.scrollLeft,
    };
    el.classList.add("grabbing");
  };
  const onDrag = (event) => {
    if (!drag.current.active) return;
    event.preventDefault();
    scrollRef.current.scrollLeft =
      drag.current.scrollLeft - (event.pageX - drag.current.startX);
  };
  const endDrag = () => {
    drag.current.active = false;
    scrollRef.current?.classList.remove("grabbing");
  };

  // After a short beat, glide to the right end so the most recent date slides
  // into view (smooth native scroll).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const timer = setTimeout(() => {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    }, 350);
    return () => clearTimeout(timer);
  }, [timeLineThumbs.length]);

  return (
    <>
      <div
        className="history-time-line"
        ref={scrollRef}
        onMouseDown={startDrag}
        onMouseMove={onDrag}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        <div
          className={classnames("line", { few: timeLineThumbs.length < 4 })}
          style={{ background: color }}
        >
          {timeLineThumbs.map((thumb) => (
            <Thumb key={thumb.id} thumb={thumb} />
          ))}
        </div>
      </div>
    </>
  );
};

/*
 * Details of timeline, raised flags and comments on a mission monitoring
 */
export const MissionDetailsCard = ({
  element,
  getStatusColor,
  refresh,
  user,
  alwaysExpanded,
  acquisitionReportResults,
  resultsAvailable,
  openResultsMap,
  date: dateProp,
}) => {
  const [steps, setSteps] = useState([]);
  const [flags, setFlags] = useState([]);
  const [comments, setComments] = useState([]);
  const [uploadSession, setUploadSession] = useState(null);
  const [hccMission, setHccMission] = useState(null);
  // Detail (steps/flags/comments) is fetched on open — show a spinner until it
  // lands instead of flashing empty placeholders then popping the data in.
  const [detailsLoading, setDetailsLoading] = useState(true);

  const acquisitionReport = useMemo(() => {
    return acquisitionReportResults?.rows.find(
      ({ date }) => date === element.acquisition_date
    );
  }, [acquisitionReportResults, element.acquisition_date]);

  const date = useMemo(
    () => dateProp ?? formatDate(element.acquisition_date),
    [dateProp, element.acquisition_date]
  );
  useEffect(() => {
    const fetchHccMissionData = async () => {
      try {
        const hccMission = await fetchHccMission(user, acquisitionReport.uuid);
        setHccMission(hccMission);
      } catch {}
    };

    if (acquisitionReport?.uuid) fetchHccMissionData();
  }, [user, acquisitionReport]);

  const refreshMissionDetailsCard = () => {
    requestFetchMissionMonitoring(element, user).then((res) => {
      setSteps(res.steps);
      setFlags(res.flags);
      setComments(res.comments);
    });
  };

  useEffect(() => {
    let unmounted = false;
    setDetailsLoading(true);
    requestFetchMissionMonitoring(element, user)
      .then((res) => {
        if (!unmounted) {
          setSteps(res.steps);
          setFlags(res.flags);
          setComments(res.comments);
        }
      })
      .finally(() => {
        if (!unmounted) setDetailsLoading(false);
      });
    if (element.upload_session_id)
      fetchUploadSession({ id: element.upload_session_id }, user)
        .then((res) => {
          if (!unmounted) setUploadSession(res);
        })
        .catch((err) => {
          if (!unmounted) console.error(err);
        });

    // TO prevent memory leaks
    return () => {
      unmounted = true;
    };
  }, [element, element.upload_session_id, user]);

  const [expanded, setExpanded] = useState(alwaysExpanded ?? false);
  const [newFlagLabel, setNewFlagLabel] = useState(null);
  const [usModalIsOpen, setUsModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const componentClassNames = classnames(
    "powerdash-component mission-details-card",
    {
      expanded: expanded,
    }
  );

  const raiseFlag = () => {
    if (!newFlagLabel) return;
    const newFlag = {
      name: newFlagLabel.value,
      created_at: moment(),
      closing_date: null,
    };
    requestCreateMissionsMonitoringFlag(element, user, newFlag).then((res) =>
      setFlags([...flags, res])
    );

    setNewFlagLabel(null);
    refresh();
  };

  const resolveFlag = (flag) => {
    const closing_date = moment();
    requestUpdateMissionsMonitoringFlag(
      { id: flag.id, closing_date: closing_date },
      user
    ).then(() => refresh());
    setFlags(
      flags.map((f) => {
        if (f.id === flag.id) return { ...f, closing_date: closing_date };
        else return f;
      })
    );
  };

  const deleteFlag = (flag) => {
    requestDeleteMissionsMonitoringFlag(flag, user).then((res) =>
      setFlags(flags.filter((f) => f.id !== flag.id))
    );
  };

  const MissionDeleteModal = ({ isOpen, toggle }) => {
    return (
      <Modal isOpen={isOpen} toggle={toggle}>
        <ModalHeader toggle={toggle}>Delete Mission Monitoring</ModalHeader>
        <ModalBody>
          Do you want to delete
          <br />
          <span style={{ fontWeight: "bold" }}>
            {element.site.display_name}
            {" | "}
            {date}
          </span>{" "}
          ?<br /> This operation is irreversible.
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            onClick={() => {
              requestDeleteMissionMonitoring(element, user).then(refresh);
              toggle();
            }}
          >
            Delete
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  return (
    <>
      {uploadSession && usModalIsOpen && (
        <UploadSessionModal
          us={uploadSession}
          isOpen={usModalIsOpen}
          closeModal={() => setUsModalIsOpen(false)}
        />
      )}
      <MissionDeleteModal
        isOpen={deleteModalIsOpen}
        toggle={() => setDeleteModalIsOpen(!deleteModalIsOpen)}
      />
      <div className={componentClassNames}>
        <ComponentTitle
          icon="fa-solid fa-gears discrete-icon"
          title={`${date} | ${element.site.display_name}`}
          topRightContent={
            <>
              {resultsAvailable && (
                <Button
                  data-tooltip-id="tooltip"
                  data-tooltip-content="View results"
                  className="p-0"
                  color="link"
                  onClick={openResultsMap}
                >
                  <i className="fa-solid fa-lg fa-earth-americas" />
                </Button>
              )}
              <Badge className="status-badge" pill>
                {humanize(element.substatus)}
              </Badge>
              <Badge
                // if color is not valid "primary secondary...", the background color is overriden by the background color of the style
                color="invalid background color"
                pill
                style={{
                  background: getStatusColor(element.status),
                  color: getContrastYIQ(getStatusColor(element.status)),
                }}
                onClick={openResultsMap}
              >
                {humanize(element.status)}
              </Badge>

              {uploadSession && (
                <Button
                  size="sm"
                  color="primary"
                  onClick={() => setUsModalIsOpen(true)}
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Associated upload session"
                >
                  <i className="fa-solid fa-upload" />
                </Button>
              )}

              {hasBackofficeAccess(user.self) && (
                <CopyButton
                  value={`${window.location.origin}/monitoring/${element.id}`}
                  datatip="Copy direct url"
                />
              )}

              {hasAdminAccess(user.self) && (
                <i
                  className="fa-solid fa-lg fa-trash-can clickable"
                  style={{
                    color: MISSION_MONITORING_FLAG_COLORS.RAISED,
                  }}
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Delete Mission"
                  onClick={() => setDeleteModalIsOpen(true)}
                />
              )}

              {!alwaysExpanded && (
                <ExpandButton expanded={expanded} setExpanded={setExpanded} />
              )}
            </>
          }
        />
        {detailsLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "3em",
            }}
          >
            <i className="fa-solid fa-circle-notch fa-spin fa-2x discrete-icon" />
          </div>
        ) : (
          <Container fluid>
            <Row>
              <HistoryTimeLine
                element={element}
                color={getStatusColor(element.status)}
                flags={flags}
                steps={steps}
              />
            </Row>
            {hccMission && (
              <AcquisitionReport expanded={expanded} hccMission={hccMission} />
            )}
            <Row className="details-body">
              <Col sm="4" className="p-3 col flags-col">
                <div className="powerdash-component flags-panel">
                  <ComponentTitle
                    icon="fa-solid fa-flag discrete-icon"
                    title={`Flags${flags.length ? ` (${flags.length})` : ""}`}
                  />
                  {flags.length === 0 ? (
                    <ComponentPlaceholder text="no flags" size="fa-3x" />
                  ) : (
                    <ul className="mission-monitoring-dashboard-list flag-list">
                      {flags.map((flag) => {
                        // Resolve affordance (backoffice, open flag only) sits on
                        // the flag icon and the ✓ button — not the whole row.
                        const canResolve =
                          hasBackofficeAccess(user.self) &&
                          flag.closing_date === null;
                        return (
                          <li
                            className={classnames("flag-item", {
                              resolved: flag.closing_date,
                            })}
                            key={flag.id}
                          >
                            <i
                              className={classnames(
                                "fa-solid fa-lg fa-flag flag-icon",
                                {
                                  clickable: canResolve,
                                }
                              )}
                              style={{ color: getFlagColor(flag) }}
                              onClick={
                                canResolve ? () => resolveFlag(flag) : undefined
                              }
                              data-tooltip-id={
                                canResolve ? "tooltip" : undefined
                              }
                              data-tooltip-content={
                                canResolve ? "Mark as resolved" : undefined
                              }
                            />
                            <div className="flag-text">
                              <span className="flag-name">
                                {humanize(flag.name)}
                              </span>
                              <span className="flag-date">
                                {formatDate(flag.created_at)}
                                {flag.closing_date &&
                                  ` · resolved ${formatDate(
                                    flag.closing_date
                                  )}`}
                              </span>
                            </div>
                            <div className="flag-actions">
                              {hasBackofficeAccess(user.self) &&
                                flag.closing_date === null && (
                                  <button
                                    type="button"
                                    className="flag-action resolve"
                                    onClick={() => resolveFlag(flag)}
                                    data-tooltip-id="tooltip"
                                    data-tooltip-content="Mark as resolved"
                                  >
                                    <i className="fa-solid fa-check" />
                                  </button>
                                )}
                              {hasAdminAccess(user.self) && (
                                <button
                                  type="button"
                                  className="flag-action delete"
                                  onClick={() => deleteFlag(flag)}
                                  data-tooltip-id="tooltip"
                                  data-tooltip-content="Delete flag"
                                >
                                  <i className="fa-solid fa-trash-can" />
                                </button>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <div className="flag-raiser">
                    {hasBackofficeAccess(user.self) && (
                      <>
                        <ReactSelect
                          className="flag-select"
                          options={MISSION_MONITORING_STATIC_FLAGS.map(
                            (flag) => {
                              return { label: flag, value: flag };
                            }
                          )}
                          placeholder="Add flag"
                          value={newFlagLabel}
                          onChange={(value) => setNewFlagLabel(value)}
                          menuPlacement="top"
                        />
                        <Button
                          outline
                          color="danger"
                          onClick={() => raiseFlag()}
                        >
                          <i className="fa-solid fa-flag" /> Add
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Col>
              <Col sm="8" className="p-0 col chat-col">
                <MissionChat
                  comments={comments}
                  isBackOffice={hasBackofficeAccess(user.self)}
                  element={element}
                  user={user}
                  refresh={() => {
                    refreshMissionDetailsCard();
                    refresh();
                  }}
                />
              </Col>
            </Row>
          </Container>
        )}
      </div>
    </>
  );
};

HistoryTimeLine.propTypes = {
  element: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
};

MissionDetailsCard.propTypes = {
  element: PropTypes.object.isRequired,
  getStatusColor: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  alwaysExpanded: PropTypes.bool,
  acquisitionReportResults: PropTypes.object,
  resultsAvailable: PropTypes.bool,
  openResultsMap: PropTypes.func,
  date: PropTypes.string,
};
